import { MutationCache, QueryClient, type DefaultOptions } from "@tanstack/react-query";

/**
 * The cache the whole app shares, made in one place so the browser and the tests differ only in what they mean to
 * differ in (retries, mostly).
 *
 * The one behaviour it adds: **a write that succeeded re-asks what Joogoos is doing** (27). Anything the person
 * does may start work — Generate, a lookup, an article, a letter, an export — and the header's indicator would
 * otherwise say nothing until it next happened to ask. One rule here rather than a line in every screen that
 * starts something, so a screen written next year gets it without knowing this exists, and so nothing polls for it
 * while a tab is sitting still (17 §performance 4).
 *
 * **And it re-asks what is left to spend** (the first pilot round, 2026-10-05): a company search cost $0.25 and the
 * balance in the header stayed where it was until a reload. Which presses spend is the server's to know — a find, a
 * letter, a lookup, Generate, a review — and a list of them kept here would be the list somebody forgets to extend,
 * so every write re-asks the two reads that hold the balance: the workspace (the header, the plan) and the credits
 * (the Credits panel). Only the ones on screen are asked again (TanStack's `refetchType: "active"`); the rest are
 * marked stale and read when they are next opened, which keeps the cost of a free write to one small read.
 */
export const ACTIVITY_KEY = ["activity"] as const;

/** The reads that hold the balance (`useWorkspace`, `useCredits`), re-asked after every write with the activity. */
const MONEY_KEYS = [["workspace"], ["credits"]] as const;

export function makeQueryClient(defaultOptions: DefaultOptions): QueryClient {
  let client: QueryClient | undefined;
  const mutationCache = new MutationCache({
    onSuccess: () => {
      for (const queryKey of [ACTIVITY_KEY, ...MONEY_KEYS]) void client?.invalidateQueries({ queryKey });
    },
  });
  client = new QueryClient({ mutationCache, defaultOptions });
  return client;
}
